// The route table, and the whole of it: this client is the Store's administration console.
//
// The customer-facing pages — the storefront, checkout and `/Account/*` — are served by
// AbstractID's management app under `/Store` (see "Abstract Store (storefront)" in the repository's
// `CLAUDE.md`). A former customer path that still reaches this origin is sent there by
// `AbstractID.initialize()` before the router mounts, which is why none of them appears here.
//
// Every route below has an entry in `App.svelte`'s `adminPages` sidebar list unless it is a detail
// page reached from its own list — `AdminNavigation.test.ts` fails otherwise, because a page the
// router serves and the sidebar omits can only be found by typing its URL.

// The landing page is the overview **and** the readiness gate (Design §4.5): `Store setup` was a
// page of its own until it merged in here. It reads the same endpoint checkout consults, so it
// stays useful long after the first run rather than being a one-time wizard — which is why the
// overview merged into *it* rather than the other way round.
import AdminOverview from './pageComponents/admin/Overview.svelte';
import AdminCatalog from './pageComponents/admin/Catalog.svelte';
import AdminSettings from './pageComponents/admin/Settings.svelte';
import AdminProviders from './pageComponents/admin/Providers.svelte';
import AdminCurrencies from './pageComponents/admin/Currencies.svelte';
import AdminTaxRates from './pageComponents/admin/TaxRates.svelte';
import AdminRefundPolicy from './pageComponents/admin/RefundPolicy.svelte';
import AdminSellers from './pageComponents/admin/Sellers.svelte';
// Email (Design §11). The sender and the copy, both admin actions rather than deployments.
import AdminEmail from './pageComponents/admin/Email.svelte';
import AdminApplications from './pageComponents/admin/Applications.svelte';
// Support (Design §9.6, §13). A customer is addressed by the **user's** uuid — one customer is one
// user, so it has no external identifier of its own.
import AdminCustomers from './pageComponents/admin/Customers.svelte';
import AdminCustomer from './pageComponents/admin/Customer.svelte';
import AdminSales from './pageComponents/admin/Sales.svelte';
import AdminSaleSubscription from './pageComponents/admin/sales/Subscription.svelte';
import AdminSaleOrder from './pageComponents/admin/sales/Order.svelte';
// Analytics (Design §12). A separate page from Operations because they answer different questions:
// Operations is read during an incident, this is read on a Monday morning.
import AdminAnalytics from './pageComponents/admin/Analytics.svelte';
// Financial reports (Design §13). Beside Analytics rather than inside it, and the split is by
// source as much as by question: analytics reads the visit log and answers "what did people do",
// this reads invoices, orders and refunds and answers "what did the store take".
import AdminReports from './pageComponents/admin/Reports.svelte';
// The invoice register (Design §4.6). Deliberately not the Reports page's document list, which
// reads the ledger and so sees only invoices that were **paid** — a draft, one awaiting payment,
// one voided or written off appeared on no admin screen at all, and "what is still owing" was
// unanswerable. Same filter bar, plus the invoice's own status.
import AdminInvoices from './pageComponents/admin/Invoices.svelte';
// Operations (Design §13, §16). Nothing on this page blocks checkout, which is precisely why it
// exists: the inline reconcile keeps purchases completing with every worker stopped.
import AdminOperations from './pageComponents/admin/Operations.svelte';
// §13. Migrating the legacy store: an upload, the mapping panels, a dry run and an apply. Its own
// page because it is a procedure run once rather than a screen anybody visits twice.
import AdminMigration from './pageComponents/admin/Migration.svelte';
import AdminLogs from './pageComponents/admin/AdminLogs.svelte';

import LegacyAdminRedirect from './pageComponents/LegacyAdminRedirect.svelte';
import Handle404 from './pageComponents/404.svelte';

export default {
	'/': AdminOverview,
	'/Catalog': AdminCatalog,
	'/Settings': AdminSettings,
	'/Settings/Providers': AdminProviders,
	'/Settings/Currencies': AdminCurrencies,
	'/Settings/Tax': AdminTaxRates,
	'/Settings/Refunds': AdminRefundPolicy,
	'/Settings/Sellers': AdminSellers,
	'/Settings/Email': AdminEmail,
	'/Applications': AdminApplications,
	'/Customers': AdminCustomers,
	'/Customers/:uuid': AdminCustomer,
	'/Transactions': AdminSales,
	'/Transactions/Subscription/:uuid': AdminSaleSubscription,
	'/Transactions/Order/:uuid': AdminSaleOrder,
	'/SalesFunnel': AdminAnalytics,
	'/Reports': AdminReports,
	'/Invoices': AdminInvoices,
	'/Operations': AdminOperations,
	'/Migration': AdminMigration,
	'/Logs': AdminLogs,

	// A transition aid, not a second door. Administration lived under `/Settings/Admin/*` inside a
	// Settings sub-layout until it became this console's root navigation, and operators have those
	// URLs bookmarked and pasted into tickets. The component *replaces* the location with the new
	// path rather than rendering anything, so the old URL never shows a page of its own and the
	// address bar ends up honest. Delete once the old links have had time to die out.
	'/Settings/Admin': LegacyAdminRedirect,
	'/Settings/Admin/*': LegacyAdminRedirect,

	'*': Handle404
};
